import { useState, useRef, useEffect } from 'react'
import Avatar from '../../components/ui/Avatar'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { LIKERS_TOOLTIP_MAX, LIKE_TOOLTIP_HOVER_DELAY_MS, COLOR_SUCCESS } from '../../lib/config'
import s from '../../styles/pages/DiscussionDetailPage.module.css'

interface Liker { id: string; displayName: string; avatarUrl: string | null }

export default function LikeButton({ commentId, liked, likeCount, isAuthenticated, accessToken, onToggle, onPromptLogin }: {
  commentId: string; liked: boolean; likeCount: number
  isAuthenticated: boolean; accessToken: string | null
  onToggle: (liked: boolean, count: number) => void; onPromptLogin: () => void
}) {
  const [showTip, setShowTip] = useState(false)
  const [likers, setLikers] = useState<Liker[]>([])
  const [loadingLikers, setLoadingLikers] = useState(false)
  const [inFlight, setInFlight] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const handleEnter = () => {
    if (likeCount === 0) return
    timer.current = setTimeout(async () => {
      if (!mounted.current) return
      setShowTip(true)
      if (likers.length === 0) {
        setLoadingLikers(true)
        try {
          const d = await api.get<{ likes: Liker[] }>('comments', ENDPOINTS.comments.likes(commentId))
          if (mounted.current) setLikers(d.likes)
        } catch {}
        if (mounted.current) setLoadingLikers(false)
      }
    }, LIKE_TOOLTIP_HOVER_DELAY_MS)
  }

  const handleLeave = () => { if (timer.current) clearTimeout(timer.current); setShowTip(false) }

  const handleClick = async () => {
    if (!isAuthenticated) { onPromptLogin(); return }
    if (inFlight) return
    const newLiked = !liked; const newCount = newLiked ? likeCount + 1 : Math.max(0, likeCount - 1)
    onToggle(newLiked, newCount)
    setInFlight(true)
    try {
      const d = await api.post<{ liked: boolean; likeCount: number }>('comments', ENDPOINTS.comments.like(commentId), undefined, accessToken)
      onToggle(d.liked, d.likeCount); setLikers([])
    } catch { onToggle(!newLiked, likeCount) } finally { setInFlight(false) }
  }

  return (
    <div className={s.likeBtnWrap} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button onClick={() => void handleClick()} disabled={inFlight} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem', borderRadius: '12px', border: 'none', background: liked ? 'rgba(34,197,94,0.12)' : 'transparent', color: liked ? COLOR_SUCCESS : 'var(--text-muted)', cursor: inFlight ? 'not-allowed' : 'pointer', fontSize: '0.75rem', fontWeight: liked ? 600 : 400, transition: 'all 0.15s', opacity: inFlight ? 0.6 : 1 }}>
        <span className={s.likeIcon}>👍</span>
        {likeCount > 0 && <span>{likeCount}</span>}
      </button>
      {showTip && likeCount > 0 && (
        <div className={s.likeTooltip}>
          <div className={s.likeTooltipTitle}>{likeCount === 1 ? '1 like' : `${likeCount} likes`}</div>
          {loadingLikers ? <div className={s.likeTooltipLoading}>Loading...</div> : (
            <div className={s.likeTooltipList}>
              {likers.slice(0, LIKERS_TOOLTIP_MAX).map((l) => (
                <div key={l.id} className={s.likeTooltipRow}>
                  <Avatar name={l.displayName} avatarUrl={l.avatarUrl} size={16} />
                  <span className={s.likeTooltipName}>{l.displayName}</span>
                </div>
              ))}
              {likeCount > LIKERS_TOOLTIP_MAX && <div className={s.likeTooltipMore}>+{likeCount - LIKERS_TOOLTIP_MAX} more</div>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
